'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function BenefitsSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.animate-on-scroll')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="benefices" ref={sectionRef} className="py-28 md:py-40 relative z-10 bg-white border-t border-primary/8">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Header */}
        <div className="mb-16 animate-on-scroll anim-in" style={{ animationPlayState: 'paused' }}>
          <span className="text-[10px] uppercase tracking-ultra text-accent font-medium block mb-5">
            Les bénéfices concrets
          </span>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-tight text-foreground max-w-2xl">
            Ce que ça change,{' '}
            <span className="italic text-sand-gradient">concrètement.</span>
          </h2>
        </div>

        {/* Bento grid — asymmetric */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Card 1 — Large, spans 7 cols */}
          <div
            className="md:col-span-7 relative overflow-hidden border border-primary/12 bg-background animate-on-scroll anim-in delay-100 group card-lift"
            style={{ animationPlayState: 'paused' }}>
            
            <div className="p-10 md:p-12">
              <div className="flex items-start justify-between mb-8">
                <div className="w-12 h-12 bg-primary/8 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" />
                  </svg>
                </div>
                <span className="text-[9px] uppercase tracking-ultra text-foreground-subtle">01</span>
              </div>
              <h3 className="font-serif-display text-3xl md:text-4xl text-foreground mb-5 leading-tight">
                Plus de clarté<br />
                <span className="italic text-primary">pour vos voyageurs</span>
              </h3>
              <p className="text-sm text-foreground-muted leading-relaxed max-w-md">
                Chaque information est organisée, accessible et claire. Vos voyageurs arrivent préparés, 
                savent exactement quoi faire, et profitent pleinement de leur séjour dès le premier instant.
              </p>
              <div className="mt-10 grid grid-cols-2 gap-6 pt-8 border-t border-primary/10">
                <div>
                  <p className="font-serif-display text-3xl text-primary mb-1">4.8<span className="text-lg text-foreground-subtle">/5</span></p>
                  <p className="text-[10px] uppercase tracking-wide text-foreground-subtle">Satisfaction voyageur moyenne</p>
                </div>
                <div>
                  <p className="font-serif-display text-3xl text-primary mb-1">−62<span className="text-lg text-foreground-subtle">%</span></p>
                  <p className="text-[10px] uppercase tracking-wide text-foreground-subtle">Questions à l'arrivée</p>
                </div>
              </div>
            </div>
            {/* Decorative corner */}
            <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-primary/4 rounded-full group-hover:scale-150 transition-transform duration-700" />
          </div>

          {/* Card 2 — spans 5 cols */}
          <div
            className="md:col-span-5 border border-accent/20 bg-accent/4 p-10 animate-on-scroll anim-in delay-200 group card-lift relative overflow-hidden"
            style={{ animationPlayState: 'paused' }}>
            
            <div className="flex items-start justify-between mb-8">
              <div className="w-12 h-12 bg-accent/15 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-accent-dark">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.09a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 15z" />
                </svg>
              </div>
              <span className="text-[9px] uppercase tracking-ultra text-foreground-subtle">02</span>
            </div>
            <h3 className="font-serif-display text-3xl text-foreground mb-5 leading-tight">
              Moins d'appels<br />
              <span className="italic text-accent-dark">pour des détails</span>
            </h3>
            <p className="text-sm text-foreground-muted leading-relaxed">
              Le code Wifi, les horaires de collecte, le fonctionnement de la climatisation — 
              toutes ces questions disparaissent. Vous reprenez votre temps.
            </p>
            <div className="absolute -bottom-8 -right-8 w-24 h-24 bg-accent/10 rounded-full group-hover:scale-150 transition-transform duration-700" />
          </div>

          {/* Card 3 — full width bottom */}
          <div
            className="md:col-span-12 relative overflow-hidden animate-on-scroll anim-in delay-300 group"
            style={{ animationPlayState: 'paused' }}>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-primary/12">
              {/* Image side */}
              <div className="relative h-64 md:h-auto image-zoom">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1cd812da6-1766548881075.png"
                  alt="Location saisonnière moderne et bien équipée en Guadeloupe avec terrasse"
                  fill
                  className="object-cover" />
                
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/20 md:to-white" />
              </div>
              {/* Content side */}
              <div className="p-10 md:p-14 bg-background flex flex-col justify-center">
                <span className="text-[9px] uppercase tracking-ultra text-foreground-subtle mb-6">03</span>
                <h3 className="font-serif-display text-3xl md:text-4xl text-foreground mb-5 leading-tight">
                  Une image plus<br />
                  <span className="italic text-teal-gradient">professionnelle</span>
                </h3>
                <p className="text-sm text-foreground-muted leading-relaxed mb-8">
                  Un accueil soigné, structuré et digital renvoie une image de sérieux et d'attention. 
                  Vos voyageurs laissent de meilleures évaluations, et votre annonce en bénéficie directement.
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex">
                    {[1, 2, 3, 4, 5]?.map((s) =>
                    <svg key={s} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#C8A96E" stroke="#C8A96E" strokeWidth="1" className="text-accent">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    )}
                  </div>
                  <span className="text-xs text-foreground-muted">Meilleures évaluations, meilleur classement</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}